import { useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Avatar as MuiAvatar,
  Box,
  Skeleton,
  styled,
  Typography,
  CardProps,
} from "@mui/material";
import { Patient } from "@interfaces";
import { ObscuredText, SecondaryActionButton } from "@components";
import { getBirthdateDetails } from "@utils";
import { format } from "rut.js";
import { Card as MuiCard, CardHeader as MuiCardHeader } from "@mui/material";
import { PersonOutlined } from "@mui/icons-material";

const Card = styled(MuiCard)({
  display: "flex",
  flexDirection: "column",
  padding: "24px 16px",
  gap: 16,
  width: "100%",
});

const CardHeader = styled(MuiCardHeader)({
  padding: 0,
});

const Avatar = styled(MuiAvatar)(({ theme }) => ({
  backgroundColor: theme.palette.primary.extralight,
}));

const SubTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  ...theme.typography.body2,
}));

const Title = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  ...theme.typography.body1,
}));

const ObscuredTitle = (props: any) => (
  <ObscuredText component={Title} {...props} />
);

const ManageRequestContainer = styled(Box)({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  marginTop: "16px",
});

interface Props extends CardProps {
  patient: Patient;
  prevRoute?: string;
  requestId?: string;
  canEdit?: boolean;
  loading?: boolean;
}

export const PatientSummary: React.FC<Props> & {
  Skeleton: React.FC<CardProps>;
} = ({
  patient,
  prevRoute = "/app/nuevo-documento/paciente",
  requestId,
  canEdit,
  ...props
}) => {
  const navigate = useNavigate();

  const patientFullName = useMemo(() => {
    return `${patient.firstSurname} ${patient.secondSurname}, ${patient.name}`;
  }, [patient]);
  const birthdateDetails = useMemo(() => {
    return getBirthdateDetails(patient.birthdate);
  }, [patient.birthdate]);

  const handleEditClick = useCallback(() => {
    navigate("/app/nuevo-documento/paciente/editar", {
      state: {
        patient,
        prevRoute,
        requestId,
      },
    });
  }, [navigate, patient, prevRoute, requestId]);

  return (
    <Card elevation={0} {...props}>
      <CardHeader
        avatar={
          <Avatar aria-label="paciente">
            <PersonOutlined
              sx={(theme) => ({ color: theme.palette.icon.primary })}
            />
          </Avatar>
        }
        title={<Title>Paciente</Title>}
        subheader={
          <ObscuredText component={SubTitle}>{patientFullName}</ObscuredText>
        }
      />
      {/* Rut or Passport */}
      <Box>
        <SubTitle>
          {patient.rut ? "Rut" : patient.passport && "Pasaporte"}
        </SubTitle>
        <ObscuredText component={Title}>
          {" "}
          {patient.rut
            ? format(patient.rut)
            : patient.passport
              ? `${patient.passport} (pasaporte)`
              : ""}
        </ObscuredText>
      </Box>
      {/* Birthdate */}
      <Box>
        <SubTitle>Fecha de nacimiento</SubTitle>
        <ObscuredText component={Title}>
          {birthdateDetails.age
            ? `${birthdateDetails.formattedBirthdate} · ${birthdateDetails.age}`
            : birthdateDetails.formattedBirthdate}
        </ObscuredText>
      </Box>

      {patient.weight ? (
        <Box>
          <SubTitle>Peso</SubTitle>
          <ObscuredText component={Title}>{patient.weight} kg</ObscuredText>
        </Box>
      ) : null}
      {/* Contact Info */}
      <Typography variant="body1" fontWeight={500}>
        Datos de contacto
      </Typography>
      <Box>
        <SubTitle>Email</SubTitle>
        <ObscuredTitle>{patient.email}</ObscuredTitle>
      </Box>
      {patient.phone && (
        <Box>
          <SubTitle>Teléfono</SubTitle>
          <ObscuredTitle>{patient.phone}</ObscuredTitle>
        </Box>
      )}
      <Box>
        <SubTitle>Dirección</SubTitle>
        <ObscuredTitle>
          {patient.street}, {patient.commune}
        </ObscuredTitle>
      </Box>
      <Box>
        <SubTitle>Región</SubTitle>
        <ObscuredTitle>{patient.region}</ObscuredTitle>
      </Box>
      <Box>
        <SubTitle>Comuna</SubTitle>
        <ObscuredTitle>{patient.commune}</ObscuredTitle>
      </Box>
      {/* Edit button */}
      {canEdit && (
        <ManageRequestContainer>
          <SecondaryActionButton onClick={handleEditClick}>
            Editar datos
          </SecondaryActionButton>
        </ManageRequestContainer>
      )}
    </Card>
  );
};

PatientSummary.Skeleton = (props: CardProps) => {
  return (
    <Card elevation={0} {...props}>
      <CardHeader
        avatar={<Skeleton variant="circular" width={40} height={40} />}
        title={<Skeleton variant="text" width="40%" />}
        subheader={<Skeleton variant="text" width="70%" />}
      />
      <Box>
        <SubTitle>
          <Skeleton variant="text" width="30%" />
        </SubTitle>
        <Title>
          <Skeleton variant="text" width="60%" />
        </Title>
      </Box>
      <Box>
        <SubTitle>
          <Skeleton variant="text" width="40%" />
        </SubTitle>
        <Title>
          <Skeleton variant="text" width="65%" />
        </Title>
      </Box>
      <Typography variant="body1" fontWeight={500}>
        <Skeleton variant="text" width="50%" />
      </Typography>
      <Box>
        <SubTitle>
          <Skeleton variant="text" width="30%" />
        </SubTitle>
        <Title>
          <Skeleton variant="text" width="70%" />
        </Title>
      </Box>
      <Box>
        <SubTitle>
          <Skeleton variant="text" width="30%" />
        </SubTitle>
        <Title>
          <Skeleton variant="text" width="55%" />
        </Title>
      </Box>
      <Box>
        <SubTitle>
          <Skeleton variant="text" width="30%" />
        </SubTitle>
        <Title>
          <Skeleton variant="text" width="80%" />
        </Title>
      </Box>
      <Box>
        <SubTitle>
          <Skeleton variant="text" width="30%" />
        </SubTitle>
        <Title>
          <Skeleton variant="text" width="40%" />
        </Title>
      </Box>
      <Box>
        <SubTitle>
          <Skeleton variant="text" width="30%" />
        </SubTitle>
        <Title>
          <Skeleton variant="text" width="35%" />
        </Title>
      </Box>
      <ManageRequestContainer>
        <Skeleton variant="rectangular" width={160} height={36} />
      </ManageRequestContainer>
    </Card>
  );
};
